import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";

const title = "Swedish National Agency for Education | SIS Estepona";
const description =
  "About Skolverket, the Swedish National Agency for Education, and supplementary Swedish tuition for pupils abroad – language and cultural understanding.";

export const Route = createFileRoute("/en/swedish-national-agency-for-education")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/swedish-national-agency-for-education") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/swedish-national-agency-for-education"),
  }),
  component: Skolverket,
});

function Skolverket() {
  return (
    <>
      <PageHero
        eyebrow="Authority"
        title="The Swedish National Agency for Education and supplementary Swedish tuition"
        intro="The Swedish National Agency for Education (Skolverket) is the national authority responsible for developing, supporting and evaluating the education system in Sweden, covering preschool, primary school, secondary school and adult education."
      />

      <Prose>
        <h2>The Agency's main tasks</h2>
        <ul>
          <li>
            <strong>Developing curricula and syllabuses:</strong> The Agency designs and revises
            national curricula and syllabuses to ensure teaching maintains a high standard and
            remains relevant to today's society.
          </li>
          <li>
            <strong>Support for schools and teachers:</strong> The Agency provides resources,
            training and guidance for teachers and school leaders to improve teaching methods and
            school development.
          </li>
          <li>
            <strong>Evaluation and quality assurance:</strong> By following up and evaluating
            schools' results and working methods, good examples are identified and shared while
            shortcomings are addressed.
          </li>
          <li>
            <strong>Rules and guidelines:</strong> The Agency is responsible for implementing and
            overseeing the laws and regulations that govern Swedish education.
          </li>
        </ul>

        <h2>Supplementary Swedish tuition</h2>
        <p>
          For pupils who live abroad or need extra support in the Swedish language, we offer
          supplementary Swedish tuition. The teaching aims to strengthen pupils' knowledge of the
          Swedish language and Swedish culture, which is crucial for maintaining and developing their
           language skills and cultural identity.
        </p>
        <p>
          We are approved by Skolverket for supplementary Swedish, and that tuition follows the
          agency's syllabus for Swedish pupils abroad. The approval covers supplementary Swedish
          only — the school itself is a private school supervised by the Junta de Andalucía (the
          Spanish education authority for Andalusia) and is not a state-funded Swedish school abroad.
        </p>
        <ul>
          <li>
            <strong>Language development:</strong> The courses focus on reading, writing, speaking
            and listening using modern and interactive teaching methods.
          </li>
          <li>
            <strong>Cultural understanding:</strong> The teaching includes elements that highlight
            Swedish culture, history and society.
          </li>
          <li>
            <strong>Tailored programmes:</strong> Flexible programmes that can be adapted to pupils'
            individual needs, including both physical and digital classes as well as individual and
            group-based learning environments.
          </li>
          <li>
            <strong>Support and guidance:</strong> Our experienced teachers provide ongoing feedback
            and support to help pupils overcome challenges and reach their personal goals.
          </li>
        </ul>
        <p>
          <a
            href="https://www.skolverket.se/publikationsserier/styrdokument/2011/kursplan-i-svenska-for-kompletterande-undervisning-for-svenska-elever-i-utlandet?id=2718"
            target="_blank"
            rel="noopener noreferrer"
          >
            Read more about the Swedish National Agency for Education here
          </a>
        </p>

        <h2>What this means for your family</h2>
        <p>
          For Swedish families abroad, the link to the Swedish school system is often decisive. Teaching
          that follows the Swedish curriculum makes it easier to return to a school in Sweden, and for
          older pupils it is the distance schools Sofia Distans and Hermods that issue grades under
          Swedish rules.
        </p>
        <p>
          If you want to know what applies in your case — for example regarding state funding or
          eligibility — the agency's own information is always what counts. We are happy to point you to
          the right page and to describe how the setup works with us.
        </p>

        <h2>Read on</h2>
        <ul>
          <li>
            <a className="text-primary link-quiet" href="/en/secondary-school-sixth-form">Lower and upper secondary</a>{" "}
            — how distance study works at our premises.
          </li>
          <li>
            <a className="text-primary link-quiet" href="/en/grade-levels">Grade levels</a> — which
            stages we take.
          </li>
          <li>
            <a className="text-primary link-quiet" href="/en/fees">Fees</a> — costs per term and
            options.
          </li>
        </ul>


      </Prose>

      <CtaBand />
      <div className="h-20" />
    </>
  );
}
